import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Building, MapPin, DollarSign, User, Mail, Phone, RefreshCw, Plus, Eye, FileText, X, ExternalLink } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface InternalJob {
  id: number;
  user_id: number;
  title: string;
  company: string;
  location: string;
  salary?: string;
  description: string;
  requirements?: string;
  industry?: string;
  department?: string;
  contact_email: string;
  contact_phone?: string;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string;
  views_count: number;
  applications_count: number;
  created_at: string;
  updated_at: string;
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
}

const InternalVacanciesPage: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<InternalJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<InternalJob | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 1 });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [applications, setApplications] = useState<any[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    title: '',
    company: '',
    location: '',
    salary: '',
    description: '',
    requirements: '',
    keySkills: '',
    industry: '',
    department: '',
    contactEmail: '',
    contactPhone: '',
    contactTelegram: ''
  });

  const industries = [
    'IT и технологии',
    'Финансы и банки',
    'Маркетинг и реклама',
    'Продажи',
    'HR и рекрутинг',
    'Производство',
    'Медицина',
    'Образование',
    'Консалтинг',
    'Недвижимость'
  ];

  const departments = [
    'Юристы',
    'Продажи',
    'Проектные менеджеры',
    'Продакт менеджеры',
    'Бухгалтеры',
    'Разработчики',
    'Дизайнеры',
    'Маркетологи',
    'HR специалисты',
    'Аналитики'
  ];

  useEffect(() => {
    loadJobs();
    loadStats();
  }, [filterStatus, pagination.page]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.page,
        limit: 50
      };
      
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const response = await api.get('/admin/internal-vacancies', { params });
      
      if (response.data.success) {
        setJobs(response.data.jobs || []);
        setPagination(response.data.pagination || { total: 0, page: 1, limit: 50, totalPages: 1 });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки вакансий');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/admin/internal-vacancies/stats/overview');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error: any) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  const handleApprove = async (jobId: number) => {
    try {
      const response = await api.post(`/admin/internal-vacancies/${jobId}/approve`);
      if (response.data.success) {
        toast.success('Вакансия одобрена');
        loadJobs();
        loadStats();
        setSelectedJob(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при одобрении вакансии');
    }
  };

  const handleReject = async (jobId: number) => {
    if (!rejectionReason.trim()) {
      toast.error('Укажите причину отклонения');
      return;
    }
    
    try {
      const response = await api.post(`/admin/internal-vacancies/${jobId}/reject`, {
        reason: rejectionReason.trim()
      });
      if (response.data.success) {
        toast.success('Вакансия отклонена');
        loadJobs();
        loadStats();
        setRejectionReason('');
        setSelectedJob(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при отклонении вакансии');
    }
  };

  const handleUnpublish = async (jobId: number) => {
    if (!window.confirm('Вы уверены, что хотите снять эту вакансию с публикации?')) {
      return;
    }
    
    try {
      const response = await api.post(`/admin/internal-vacancies/${jobId}/unpublish`);
      if (response.data.success) {
        toast.success('Вакансия снята с публикации');
        loadJobs();
        loadStats();
        setSelectedJob(null);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при снятии вакансии с публикации');
    }
  };

  const loadApplications = async (jobId: number) => {
    try {
      setLoadingApplications(true);
      const response = await api.get(`/admin/internal-vacancies/${jobId}/applications`);
      if (response.data.success) {
        setApplications(response.data.applications || []);
        setShowApplicationsModal(true);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки откликов');
    } finally {
      setLoadingApplications(false);
    }
  };

  const handleCreateJob = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!createFormData.title?.trim() || !createFormData.company?.trim() || !createFormData.location?.trim() || !createFormData.description?.trim()) {
      toast.error('Заполните обязательные поля: название, компания, город, описание');
      return;
    }

    try {
      const response = await api.post('/admin/internal-vacancies', {
        ...createFormData,
        salary: createFormData.salary || null,
        requirements: createFormData.requirements || null,
        keySkills: createFormData.keySkills?.trim() || null,
        industry: createFormData.industry || null,
        department: createFormData.department || null,
        contactEmail: createFormData.contactEmail?.trim() || null,
        contactPhone: createFormData.contactPhone?.trim() || null,
        contactTelegram: createFormData.contactTelegram?.trim() || null
      });
      if (response.data.success) {
        toast.success('Вакансия создана и одобрена');
        setShowCreateModal(false);
        setCreateFormData({
          title: '',
          company: '',
          location: '',
          salary: '',
          description: '',
          requirements: '',
          keySkills: '',
          industry: '',
          department: '',
          contactEmail: '',
          contactPhone: '',
          contactTelegram: ''
        });
        loadJobs();
        loadStats();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при создании вакансии');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'На модерации';
      case 'approved': return 'Одобрена';
      case 'rejected': return 'Отклонена';
      default: return 'Неизвестно';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'сегодня';
    if (days === 1) return 'вчера';
    if (days < 7) return `${days} дня назад`;
    if (days < 30) return `${Math.floor(days / 7)} недели назад`;
    return date.toLocaleDateString('ru-RU');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Модерация внутренних вакансий</h1>
            <p className="text-gray-600">Проверка и одобрение вакансий, созданных пользователями</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Добавить вакансию</span>
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">На модерации</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pending_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Одобрено</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.approved_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <XCircle className="h-8 w-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Отклонено</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.rejected_count || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <Building className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Всего</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_count || 0}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  setFilterStatus('all');
                  setPagination({ ...pagination, page: 1 });
                }}
                className={`px-4 py-2 rounded-lg font-medium ${
                  filterStatus === 'all' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Все
              </button>
              <button
                onClick={() => {
                  setFilterStatus('pending');
                  setPagination({ ...pagination, page: 1 });
                }}
                className={`px-4 py-2 rounded-lg font-medium ${
                  filterStatus === 'pending' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                На модерации
              </button>
              <button
                onClick={() => {
                  setFilterStatus('approved');
                  setPagination({ ...pagination, page: 1 });
                }}
                className={`px-4 py-2 rounded-lg font-medium ${
                  filterStatus === 'approved' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Одобрено
              </button>
              <button
                onClick={() => {
                  setFilterStatus('rejected');
                  setPagination({ ...pagination, page: 1 });
                }}
                className={`px-4 py-2 rounded-lg font-medium ${
                  filterStatus === 'rejected' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Отклонено
              </button>
            </div>
            <button
              onClick={() => {
                loadJobs();
                loadStats();
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Обновить</span>
            </button>
          </div>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Загрузка вакансий...</p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Вакансии не найдены</h3>
            <p className="text-gray-500">Нет вакансий с выбранным статусом</p>
          </div>
        ) : (
          <div className="space-y-6">
            {jobs.map(job => (
              <div key={job.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
                        {getStatusText(job.status)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3 flex-wrap gap-2">
                      <div className="flex items-center">
                        <Building className="h-4 w-4 mr-1" />
                        {job.company}
                      </div>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        {job.location}
                      </div>
                      {job.salary && (
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 mr-1" />
                          {job.salary}
                        </div>
                      )}
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        {job.user_first_name && job.user_last_name 
                          ? `${job.user_first_name} ${job.user_last_name}`
                          : job.user_email || 'Неизвестно'}
                      </div>
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {formatDate(job.created_at)}
                      </div>
                    </div>
                    <p className="text-gray-700 mb-4 whitespace-pre-wrap">{job.description}</p>
                    {job.requirements && (
                      <div className="mb-4">
                        <h4 className="font-medium text-gray-900 mb-2">Требования:</h4>
                        <p className="text-gray-700 whitespace-pre-wrap">{job.requirements}</p>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {job.industry && (
                        <span className="bg-gray-100 px-2 py-1 rounded text-sm">{job.industry}</span>
                      )}
                      {job.department && (
                        <span className="bg-gray-100 px-2 py-1 rounded text-sm">{job.department}</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-1" />
                        {job.contact_email}
                      </div>
                      {job.contact_phone && (
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-1" />
                          {job.contact_phone}
                        </div>
                      )}
                    </div>
                    {job.rejection_reason && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center mb-1">
                          <XCircle className="h-4 w-4 text-red-600 mr-2" />
                          <span className="font-medium text-red-800">Причина отклонения:</span>
                        </div>
                        <p className="text-red-700">{job.rejection_reason}</p>
                      </div>
                    )}
                  </div>
                  <div className="ml-6 flex flex-col space-y-2">
                    <button
                      onClick={() => navigate(`/vacancies/internal/${job.id}`)}
                      className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      <span>Подробнее</span>
                    </button>
                    {job.status === 'pending' && (
                      <>
                        <button
                          onClick={() => setSelectedJob(job)}
                          className="bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                        >
                          <CheckCircle className="h-4 w-4" />
                          <span>Одобрить</span>
                        </button>
                        <button
                          onClick={() => setSelectedJob(job)}
                          className="bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                        >
                          <XCircle className="h-4 w-4" />
                          <span>Отклонить</span>
                        </button>
                      </>
                    )}
                    {job.status === 'approved' && (
                      <>
                        <button
                          onClick={() => handleUnpublish(job.id)}
                          className="bg-yellow-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-yellow-700 transition-colors flex items-center justify-center space-x-2"
                        >
                          <XCircle className="h-4 w-4" />
                          <span>Снять с публикации</span>
                        </button>
                        <button
                          onClick={() => loadApplications(job.id)}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                          disabled={loadingApplications}
                        >
                          <Eye className="h-4 w-4" />
                          <span>Отклики ({job.applications_count || 0})</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-8 flex justify-center space-x-2">
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
              disabled={pagination.page === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Назад
            </button>
            <span className="px-4 py-2 bg-white border border-gray-300 rounded-lg">
              Страница {pagination.page} из {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
              disabled={pagination.page >= pagination.totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Вперед
            </button>
          </div>
        )}

        {/* Modal for Approval/Rejection */}
        {selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {selectedJob.status === 'pending' ? 'Действие с вакансией' : 'Информация о вакансии'}
              </h3>
              
              <div className="mb-4">
                <p className="font-medium text-gray-900">{selectedJob.title}</p>
                <p className="text-sm text-gray-600">{selectedJob.company}</p>
              </div>

              {selectedJob.status === 'pending' && (
                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleApprove(selectedJob.id)}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      <span>Одобрить</span>
                    </button>
                    <button
                      onClick={() => setSelectedJob(null)}
                      className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                    >
                      Отмена
                    </button>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Причина отклонения (если отклоняете):
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="Укажите причину отклонения..."
                    />
                    <button
                      onClick={() => handleReject(selectedJob.id)}
                      className="w-full mt-2 bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <XCircle className="h-4 w-4" />
                      <span>Отклонить</span>
                    </button>
                  </div>
                </div>
              )}

              {selectedJob.status !== 'pending' && (
                <button
                  onClick={() => setSelectedJob(null)}
                  className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                >
                  Закрыть
                </button>
              )}
            </div>
          </div>
        )}

        {/* Create Job Modal — поля и порядок как в ЛК (CreateJob) */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-semibold mb-6">Создать вакансию</h2>
                <form onSubmit={handleCreateJob} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Название вакансии *
                      </label>
                      <input
                        type="text"
                        value={createFormData.title}
                        onChange={(e) => setCreateFormData({ ...createFormData, title: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Например: Senior Frontend Developer"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Компания *
                      </label>
                      <input
                        type="text"
                        value={createFormData.company}
                        onChange={(e) => setCreateFormData({ ...createFormData, company: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Название компании"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Город *
                      </label>
                      <input
                        type="text"
                        value={createFormData.location}
                        onChange={(e) => setCreateFormData({ ...createFormData, location: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Москва"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Зарплата
                      </label>
                      <input
                        type="text"
                        value={createFormData.salary}
                        onChange={(e) => setCreateFormData({ ...createFormData, salary: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="150 000 - 200 000 ₽"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Отрасль
                      </label>
                      <select
                        value={createFormData.industry}
                        onChange={(e) => setCreateFormData({ ...createFormData, industry: e.target.value })}
                        className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        style={{ paddingRight: '3rem' }}
                      >
                        <option value="">Выберите отрасль</option>
                        {industries.map((ind) => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Направление
                      </label>
                      <select
                        value={createFormData.department}
                        onChange={(e) => setCreateFormData({ ...createFormData, department: e.target.value })}
                        className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        style={{ paddingRight: '3rem' }}
                      >
                        <option value="">Выберите направление</option>
                        {departments.map((dep) => (
                          <option key={dep} value={dep}>{dep}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Описание вакансии *
                    </label>
                    <textarea
                      value={createFormData.description}
                      onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={6}
                      placeholder="Опишите обязанности, задачи и особенности работы..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Требования
                    </label>
                    <textarea
                      value={createFormData.requirements}
                      onChange={(e) => setCreateFormData({ ...createFormData, requirements: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={4}
                      placeholder="Опыт работы, образование, личные качества..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Навыки (в карточке вакансии)
                    </label>
                    <input
                      type="text"
                      value={createFormData.keySkills}
                      onChange={(e) => setCreateFormData({ ...createFormData, keySkills: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="SQL, Python, Excel (через запятую)"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Ключевые навыки через запятую — отобразятся тегами в списке вакансий
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email для связи
                      </label>
                      <input
                        type="email"
                        value={createFormData.contactEmail}
                        onChange={(e) => setCreateFormData({ ...createFormData, contactEmail: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="hr@company.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Телефон для связи
                      </label>
                      <input
                        type="tel"
                        value={createFormData.contactPhone}
                        onChange={(e) => setCreateFormData({ ...createFormData, contactPhone: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="+7 (999) 123-45-67"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telegram (@nickname)
                      </label>
                      <input
                        type="text"
                        value={createFormData.contactTelegram}
                        onChange={(e) => setCreateFormData({ ...createFormData, contactTelegram: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="@nickname"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-4 pt-2 border-t">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateModal(false);
                        setCreateFormData({
                          title: '',
                          company: '',
                          location: '',
                          salary: '',
                          description: '',
                          requirements: '',
                          keySkills: '',
                          industry: '',
                          department: '',
                          contactEmail: '',
                          contactPhone: '',
                          contactTelegram: ''
                        });
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Создать
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InternalVacanciesPage;

